<template>
  <div class="container">
    <div class="grid justify-content-center">
      <div class="col-12  comp-grid">
        <div class="card">
          <div v-if="payment_status != 'Paid'" class="grid">
            <div class="col-12 md:col-12 comp-grid" v-if="!transaction_id">

              <!-- Instructions (shown only on step 1) -->
              <div class="full-width">
                <div class="text-purple-500 mt-2">
                  <div class="font-bold text-yellow-500 mb-2">
                    {{ $t("Payment Instructions") }}
                  </div>
                  <ul>
                    <li>{{ $t("all_fields_marked_are_mandatory") }}</li>
                    <li>
                      {{ $t("select_any_of_the_payment_methods_available_and_provide_required_information") }}
                    </li>
                    <li>
                      {{ $t("This online purchase will incur transaction charges") }}
                    </li>
                    <li>
                      <span class="text-pink-500 font-bold">Please make sure you have enough balance on the Mobile or
                        Card Wallet.</span>
                    </li>
                    <li v-if="msisdn">
                      {{ $t("Your Wallet number") }} is {{ msisdn }}
                    </li>
                  </ul>

                  <section class="page-section mb-3">
                    <div class="container">
                      <div class="card bg-blue-100">
                        <div class="text-primary font-semibold">
                          Note: Voucher purchased will only be usable for applications within the chosen academic year.
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <!-- Step Indicator -->
              <div class="full-width">
                <div class="flex align-items-center justify-content-center mb-4 mt-2">
                  <template v-for="(step, i) in steps" :key="step.num">
                    <div class="flex flex-column align-items-center" style="min-width:60px">
                      <div class="step-circle flex align-items-center justify-content-center border-circle font-bold"
                        :class="{
                          'step-active': currentStep === step.num,
                          'step-done': currentStep > step.num,
                          'step-pending': currentStep < step.num
                        }">
                        <i v-if="currentStep > step.num" class="pi pi-check" style="font-size:0.8rem"></i>
                        <span v-else>{{ step.num }}</span>
                      </div>
                      <div class="text-xs mt-1 text-center step-label"
                        :class="currentStep === step.num ? 'font-bold text-primary' : 'text-500'">
                        {{ step.label }}
                      </div>
                    </div>
                    <div v-if="i < steps.length - 1" class="step-line mb-3"></div>
                  </template>
                </div>

                <!-- STEP 1: Applicant Type -->
                <div v-if="currentStep === 1">
                  <div class="text-lg font-bold text-primary mb-1">Step 1: Applicant Type</div>
                  <div class="text-500 text-sm mb-3">Select the option that best describes you.</div>
                  <div class="grid">
                    <div v-for="option in $menus.applicantTypeItems" :key="option.value" class="col-12 md:col-6">
                      <div class="sel-card p-4 border-2 border-round cursor-pointer text-center"
                        :class="formData.applicant_type === option.value ? 'sel-card--on' : 'sel-card--off'"
                        @click="formData.applicant_type = option.value">
                        <i class="pi pi-user text-3xl mb-2 block"
                          :class="formData.applicant_type === option.value ? 'text-primary' : 'text-400'"></i>
                        <div class="font-bold text-base">{{ option.label }}</div>
                        <div class="text-xs text-500 mt-1">
                          <span v-if="option.value === 'Regular'">Ghanaian citizens applying domestically</span>
                          <span v-else>Non-Ghanaian / International applicants</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="stepTouched && !formData.applicant_type" class="text-red-500 text-sm mt-2">
                    Please select an applicant type.
                  </div>
                  <div class="flex justify-content-end mt-4">
                    <Button label="Next" icon="pi pi-arrow-right" iconPos="right" class="p-button-primary"
                      @click="goNext()" />
                  </div>
                </div>

                <!-- STEP 2: Form Type (Undergraduate/Postgraduate) -->
                <div v-if="currentStep === 2">
                  <div class="text-lg font-bold text-primary mb-1">Step 2: Form Type</div>
                  <div class="text-500 text-sm mb-3">Select the type of application form you need.</div>
                  <div class="grid">
                    <div v-for="option in $menus.formTypeItems" :key="option.value" class="col-12 md:col-6">
                      <div class="sel-card p-4 border-2 border-round cursor-pointer text-center"
                        :class="formData.form_type === option.value ? 'sel-card--on' : 'sel-card--off'"
                        @click="formData.form_type = option.value">
                        <i class="pi pi-book text-3xl mb-2 block"
                          :class="formData.form_type === option.value ? 'text-primary' : 'text-400'"></i>
                        <div class="font-bold text-base">{{ option.label }}</div>
                        <div class="text-xs text-500 mt-1">
                          <span v-if="option.value === 'Undergraduate'">Bachelor's degree and Diploma programmes</span>
                          <span v-else>Master's, PhD, and Other advanced programmes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="stepTouched && !formData.form_type" class="text-red-500 text-sm mt-2">
                    Please select a form type.
                  </div>
                  <div class="flex justify-content-between mt-4">
                    <Button label="Back" icon="pi pi-arrow-left" class="p-button-secondary p-button-outlined"
                      @click="goBack()" />
                    <Button label="Next" icon="pi pi-arrow-right" iconPos="right" class="p-button-primary"
                      @click="goNext()" />
                  </div>
                </div>

                <!-- STEP 2: Application Form Type -->
                <div v-if="currentStep === 3">
                  <div class="text-lg font-bold text-primary mb-1">Step 3: Select Your Form</div>
                  <div class="text-500 text-sm mb-3">Choose the application form type.</div>

                  <div class="mb-2">
                    <div class="mb-2 font-bold text-sm">{{ $t('Application Form Type') }} *</div>

                    <div v-if="formTypeLoading" class="grid">
                      <div v-for="n in 2" :key="n" class="col-12 md:col-6">
                        <Skeleton height="5rem" class="border-round mb-2" />
                      </div>
                    </div>

                    <div v-else-if="formTypeOptions.length === 0"
                      class="text-orange-500 text-sm p-3 border-1 border-orange-300 border-round">
                      No application forms available for the selected options.
                    </div>

                    <div v-else class="grid">
                      <div v-for="option in formTypeOptions" :key="option.value" class="col-12 md:col-6">
                        <div class="sel-card p-3 border-2 border-round cursor-pointer"
                          :class="formData.transaction_type === option.value ? 'sel-card--on' : 'sel-card--off'"
                          @click="selectFormType(option)">
                          <div class="flex justify-content-between align-items-start">
                            <div>
                              <div class="font-bold text-sm mb-1">{{ option.label }}</div>
                              <div class="text-xs text-500">Open for <span class="font-semibold">{{
                                option.learning_institution
                              }}</span> learning</div>
                              <div class="text-xs text-400 mt-1"><span
                                  class="font-semibold">{{
                                    option.academic_month }}</span> Intake(s)</div>
                              <div class="text-xs text-400 mt-1">{{ option.formtype }} &middot; <span
                                  class="font-semibold">[{{
                                    option.academic_year }}]</span></div>
                            </div>
                            <i v-if="formData.transaction_type === option.value"
                              class="pi pi-check-circle text-primary text-xl ml-2 flex-shrink-0"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="stepTouched && !formData.transaction_type" class="text-red-500 text-sm mt-2">
                      Please select an application form type.
                    </div>
                  </div>

                  <div class="flex justify-content-between mt-4">
                    <Button label="Back" icon="pi pi-arrow-left" class="p-button-secondary p-button-outlined"
                      @click="goBack()" />
                    <Button label="Next" icon="pi pi-arrow-right" iconPos="right" class="p-button-primary"
                      @click="goNext()" />
                  </div>
                </div>

                <!-- STEP 3: Payment Method -->
                <div v-if="currentStep === 4">
                  <div class="text-lg font-bold text-primary mb-1">Step 4: Payment Method</div>
                  <div class="text-500 text-sm mb-3">Choose how you would like to pay.</div>

                  <div class="grid">
                    <div v-for="method in availablePaymentMethods" :key="method.value" class="col-12 md:col-6">
                      <div class="sel-card p-3 border-2 border-round cursor-pointer"
                        :class="formData.payment_method === method.value ? 'sel-card--on' : 'sel-card--off'"
                        @click="selectPaymentMethod(method.value)">
                        <div class="flex align-items-center">
                          <img v-if="method.image" :src="method.image" style="width:44px;height:44px;object-fit:contain"
                            class="mr-3 flex-shrink-0" :alt="method.label" />
                          <div class="flex-1">
                            <div class="font-bold text-sm">{{ method.label }}</div>
                            <div class="text-xs text-500 mt-1">
                              <span v-if="method.value !== 'CARD'">Mobile wallet payment</span>
                              <span v-else>Visa / Mastercard debit card</span>
                            </div>
                          </div>
                          <i v-if="formData.payment_method === method.value"
                            class="pi pi-check-circle text-primary text-xl ml-2 flex-shrink-0"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="stepTouched && !formData.payment_method" class="text-red-500 text-sm mt-2">
                    Please select a payment method.
                  </div>

                  <div class="flex justify-content-between mt-4">
                    <Button label="Back" icon="pi pi-arrow-left" class="p-button-secondary p-button-outlined"
                      @click="goBack()" />
                    <Button label="Next" icon="pi pi-arrow-right" iconPos="right" class="p-button-primary"
                      @click="goNext()" />
                  </div>
                </div>

                <!-- STEP 4: Payment Confirmation (contact + card/OTP details) -->
                <div v-if="currentStep === 5">
                  <div class="text-lg font-bold text-primary mb-1">Step 5: Payment Confirmation</div>
                  <div class="text-500 text-sm mb-3">
                    Applicant: <strong>{{ formData.applicant_type === 'Regular' ? 'Ghanaian' : 'International'
                    }}</strong>
                    &nbsp;&middot;&nbsp;
                    Method: <strong>{{ formData.payment_method }}</strong>
                  </div>

                  <form ref="observer" tag="form" @reset="resetForm" @submit.prevent="startPayment()">
                    <div class="card nice-shadow-5">
                      <div class="grid">

                        <div class="col-12 md:col-12">
                          <div class="mb-2 font-bold text-sm">{{ $t("email_address") }} *</div>
                          <InputText ref="ctrlpayer_email" v-model.trim="formData.payer_email"
                            :label="$t('email_address')" type="email" :placeholder="$t('email_address')" class="w-full"
                            maxlength="40" :class="getErrorClass('payer_email')" />
                        </div>

                        <div class="col-12 md:col-12">
                          <div class="mb-2 font-bold text-sm">
                            {{ $t("phone_number") }} *
                            <span class="font-normal" v-if="formData.payment_method && formData.payment_method != card">
                              (Enter your mobile wallet number and verify with OTP)
                            </span>
                          </div>
                          <div class="col-12 px-0">
                            <div class="grid">
                              <div
                                :class="[formData.payment_method && formData.payment_method != card && formData.telephone_number ? 'col-12 md:col-3' : 'col-12 md:col-4']">
                                <Dropdown class="w-full" :class="getErrorClass('dial_code')" optionLabel="dial_code"
                                  optionValue="value" ref="ctrldial_code" :filter="true"
                                  :filterFields="['name', 'dial_code']" v-model="formData.dial_code"
                                  :options="$menus.dial_codes_list" :label="$t('dial_code')"
                                  :placeholder="$t('dial_code')">
                                  <template #option="slotProps">
                                    <div class="flex align-items-center">
                                      <div>
                                        <div>{{ slotProps.option.name }}</div>
                                        <div v-if="slotProps.option.name" class="text-sm text-500">
                                          {{ slotProps.option.code }} {{ slotProps.option.dial_code }}
                                        </div>
                                      </div>
                                    </div>
                                  </template>
                                </Dropdown>
                              </div>
                              <div
                                :class="formData.payment_method && formData.payment_method != card && formData.telephone_number ? 'col-12 md:col-6' : 'col-12 md:col-8'">
                                <InputText ref="ctrltelephone_number" v-model.trim="formData.telephone_number"
                                  :label="$t('telephone_number')" type="text" :placeholder="$t('telephone_number')"
                                  pattern="^[0-9]+$" class="w-full" maxlength="10" minlength="8"
                                  :class="getErrorClass('telephone_number')" v-tooltip.top.focus="'Eg: 0209013836'" />
                              </div>
                              <div class="col-12 md:col-3"
                                v-if="formData.payment_method && formData.payment_method != card && formData.telephone_number">
                                <Button :disabled="otpSent == true" :label="$t('Send OTP')" :loading="saving"
                                  class="p-button-primary w-full" type="button" @click="sendPhoneOTP()" />
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="formData.payment_method && formData.payment_method != card && formData.telephone_number">
                            <small v-if="otpSent == true" class="text-green-500">Your OTP has been sent successfully and
                              will expire in
                              <strong>{{ countDown }}</strong></small>
                            <small v-if="otpExpired == true" class="text-pink-500">Your OTP has expired. Please request
                              a new
                              one.</small>
                          </div>
                        </div>

                        <div
                          v-if="formData.applicant_type == 'Regular' && formData.payment_method && formData.payment_method != card"
                          class="col-12">
                          <div class="mb-2 font-bold text-sm">{{ $t("OTP") }} *</div>
                          <div class="grid">
                            <div class="col-12 md:col-9">
                              <InputText class="w-full" placeholder="Enter Code Here" v-model="formData.otpCode"
                                :class="getErrorClass('otpCode')" type="text" autocomplete="off" />
                            </div>
                            <div class="col-12 md:col-3">
                              <Button :label="otpVerified ? 'Verified' : 'Verify OTP'"
                                :icon="otpVerified ? 'pi pi-check' : 'pi pi-shield'"
                                :class="otpVerified ? 'p-button-success w-full' : 'p-button-primary w-full'"
                                :disabled="!formData.otpCode || otpVerified || verifyingOtp" :loading="verifyingOtp"
                                type="button" @click="verifyOtpOnly()" />
                            </div>

                          </div>
                          <small v-if="otpVerified" class="text-green-500 mt-1 block">OTP verified successfully.</small>
                        </div>

                        <div v-if="formData.payment_method == card" class="col-12">
                          <div class="card nice-shadow-5 mx-0 mt-2">
                            <div class="grid">
                              <div class="col-12 md:col-7">
                                <div class="mb-2 font-bold text-sm">{{ $t("card_holder_name") }} *</div>
                                <InputText ref="ctrlcard_holder" v-model.trim="formData.card_holder"
                                  :label="$t('card_holder_name')" type="text" class="w-full"
                                  style="text-transform: uppercase" :class="getErrorClass('card_holder')" />
                              </div>
                              <div class="col-12 md:col-5">
                                <div class="mb-2 font-bold text-sm">{{ $t("card_number") }} *</div>
                                <InputMask ref="ctrlcard_number" v-model.trim="formData.card_number"
                                  :label="$t('card_number')" type="text" placeholder="9999 9999 9999 9999"
                                  class="w-full" mask="9999 9999 9999 9999" :class="getErrorClass('card_number')" />
                              </div>
                              <div class="col-12 md:col-12">
                                <div class="grid">
                                  <div class="col-12 md:col-4">
                                    <div class="mb-2 font-bold text-sm">{{ $t("expiry_month") }} *</div>
                                    <Dropdown class="w-full" :class="getErrorClass('expiry_month')" optionLabel="label"
                                      optionValue="value" ref="ctrlexpiry_month" v-model="formData.expiry_month"
                                      :options="$menus.expiry_monthItems" :label="$t('expiry_month')"
                                      :placeholder="$t('select')" />
                                  </div>
                                  <div class="col-12 md:col-4">
                                    <div class="mb-2 font-bold text-sm">{{ $t("expiry_year") }} *</div>
                                    <Dropdown class="w-full" :class="getErrorClass('expiry_year')" ref="ctrlexpiry_year"
                                      optionLabel="label" optionValue="value" v-model="formData.expiry_year"
                                      :options="$menus.expiry_yearItems" :label="$t('expiry_year')"
                                      :placeholder="$t('select')" />
                                  </div>
                                  <div class="col-12 md:col-4">
                                    <div class="mb-2 font-bold text-sm">{{ $t("CVV") }} *</div>
                                    <InputMask ref="ctrlcvv" v-model.trim="formData.cvv" :label="$t('cvv')" type="text"
                                      placeholder="999" pattern="^[0-9]+$" class="w-full" mask="999"
                                      :class="getErrorClass('cvv')" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>

                    <div class="flex justify-content-between mt-4">
                      <Button label="Back" icon="pi pi-arrow-left" class="p-button-secondary p-button-outlined"
                        type="button" @click="goBack()" />
                      <Button :disabled="otpExpired == true || (formData.payment_method != card && !otpVerified)"
                        type="submit" :label="$t('continue')" icon="pi pi-send" :loading="saving" />
                    </div>
                  </form>
                </div>

                <!-- Mature eligibility confirmation dialog -->
                <Dialog v-model:visible="showCriteriaDialog" modal :closable="true"
                  :header="pendingFormType ? pendingFormType.entry_level + ' — Eligibility Criteria' : 'Eligibility Criteria'"
                  style="width:520px;max-width:95vw">
                  <div class="text-sm">

                    <div class="font-bold text-primary mb-2">Minimum Age, Working Experience &amp; Academic requirements</div>

                    <!-- Mature Direct -->
                    <template v-if="pendingFormType && pendingFormType.entry_level === 'Mature Direct'">
                      <ul class="mt-0 mb-2 pl-4">
                        <li>Must be <strong>25 years or above</strong> with legitimate proof</li>
                        <li>Must have proof of at least <strong>5 years working experience</strong></li>
                        <li>Credit passes in <strong>WASSCE grade C6 or better</strong> or <strong>SSSCE grade D or better</strong> in English and Mathematics</li>
                      </ul>
                    </template>

                    <!-- Mature Exam -->
                    <template v-if="pendingFormType && pendingFormType.entry_level === 'Mature Exam'">
                      <ul class="mt-0 mb-2 pl-4">
                        <li>Must be <strong>25 years or above</strong> with legitimate proof</li>
                        <li>Must have proof of at least <strong>5 years working experience</strong></li>
                        <li>Passes in <strong>WASSCE grade E8 or better</strong> OR <strong>SSSCE grade E or better</strong> in six (6) subjects including English and Mathematics</li>
                        <li>Must pass a Mature Entrance Examination in <strong>English Language, &amp; Mathematics</strong></li>
                      </ul>
                    </template>

                    <Message class="mt-3" severity="warn" :closable="false">
                      By confirming, you declare that you meet these requirements.
                    </Message>
                  </div>

                  <template #footer>
                    <Button label="Cancel" icon="pi pi-times" class="p-button-text"
                      @click="showCriteriaDialog = false; pendingFormType = null" />
                    <Button label="I Meet These Requirements" icon="pi pi-check" class="p-button-primary"
                      @click="confirmFormTypeSelection()" />
                  </template>
                </Dialog>
                <slot :submit="submit" :saving="saving"></slot>
              </div>
            </div>

            <!-- Wallet payment pending -->
            <div class="col-12"
              v-else-if="transaction_id && transaction_message == 'WALLET' && transaction_code == '0'">
              <div class="card nice-shadow-5">
                <div class="mb-3">
                  <i style="font-size: 3em" class="pi pi-wallet"></i>
                </div>
                <div class="">
                  <div class="font-bold mb-2">{{ $t("payment_authorization_request_sent") }}</div>
                  <div class="text-purple-500">
                    <ul>
                      <li>{{ $t("to_complete_payment_you_will_receive_a_prompt_on_your_mobile_device") }}</li>
                      <li>{{ $t("after_entering_your_pin_kindly_wait_for_a_confirmation_text") }}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="mt-3 text-center">
                <Button :label="$t('i_have_paid')" class="p-button-primary px-5 pulse-button" :loading="saving"
                  @click="confirmPayment()" />
              </div>
            </div>

            <!-- Card 3DS flow -->
            <div class="col-12" v-show="transaction_id && transaction_code == '2'">
              <div class="full-width" v-if="transaction_id && transaction_code == '2'">
                <div class="text-purple-500 mt-2">
                  <div class="font-bold text-yellow-500 mb-2">{{ $t("Payment Instructions") }}</div>
                  <ul>
                    <li><span class="text-pink-500">This validation is conducted by the bank that issued your
                        card.</span></li>
                    <li>
                      <span class="text-pink-500">Disregard the <span class="font-bold">"Checking"</span> process after
                        submitting
                        your OTP and simply click the "I Have Paid" button to view your receipt.</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="flex justify-content-center flex-wrap w-full">
                <div>
                  <iframe id="cardinal_collection_iframe" name="collection-iframe" style="display: none"></iframe>
                  <form id="cardinal_collection_form" method="POST" target="collection-iframe"
                    action="{setupResponse.deviceDataCollectionUrl}">
                    <input id="cardinal_collection_form_input" type="hidden" name="JWT" value="{setupResponse.jwt}" />
                  </form>
                  <iframe id="step_up_iframe" name="step-up-iframe" height="600" width="500"
                    style="display: none"></iframe>
                  <form id="step_up_form" target="step-up-iframe" method="POST" action="{enrollResponse.stepUpUrl}">
                    <input type="hidden" name="JWT" value="{enrollResponse.jwt}" />
                    <input type="hidden" name="MD" value="{enrollResponse.customData}" />
                  </form>
                </div>
              </div>
              <div class="mt-3 text-center" v-if="transaction_id && transaction_code == '2'">
                <Button :label="$t('i_have_paid')" class="p-button-primary px-5 pulse-button" :loading="saving"
                  @click="confirmPayment()" />
              </div>
            </div>
          </div>

          <!-- Payment Success -->
          <div v-else class="grid">
            <div class="col-12 md:col-12">
              <div class="card nice-shadow-5">
                <div class="mb-3">
                  <i style="font-size: 3em" class="pi pi-check text-green-500"></i>
                </div>
                <div class="">
                  <div class="font-bold mb-2">{{ $t("Payment Successful") }}</div>
                  <Divider></Divider>
                  <div class="grid q-col-gutter-x-md">
                    <div class="col-12">
                      <div class="grid">
                        <div class="md:col-6"><span class="mr-1">{{ $t("amount") }} (GHS): </span></div>
                        <div class="md:col-6">{{ transaction_amount }}</div>
                      </div>
                      <hr />
                      <div class="grid">
                        <div class="md:col-6"><span class="mr-1">{{ $t("transaction_id") }}:</span></div>
                        <div class="md:col-6">{{ transaction_id }}</div>
                      </div>
                      <hr />
                      <div class="grid">
                        <div class="md:col-6"><span class="mr-1">{{ $t("telephone") }}: </span></div>
                        <div class="md:col-6">{{ transaction_msisdn || formData.telephone_number }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4 text-center">
                <div class="text-muted text-purple-600 mx-5 mb-3 px-2">
                  Please click the "View Receipt" button below to see your <b>Serial Number</b> and <b>Pin</b>. Do NOT
                  leave this
                  page without clicking this button.
                </div>
                <a class="p-button-primary px-5" :href="`${return_url}`" target="_blank">
                  <Button icon="pi pi-print" :label="$t('view_receipt')"
                    class="p-button-primary py-3 pulse-button"></Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { email, required, requiredIf } from "@vuelidate/validators";
import { mapActions, mapGetters, mapState } from "vuex";
import { count } from "../../../../backend/models/basemodel.js";
import { EditPageMixin } from "../../mixins/editpage.js";
import { PageMixin } from "../../mixins/page.js";
import { ApiService } from "../../services/api";
import { i18n } from "../../services/i18n.js";

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  name: "paymentApplicantsPage",
  components: {},
  mixins: [PageMixin],
  props: {
    pageName: {
      type: String,
      default: "applicants",
    },
  },
  data() {
    return {
      formData: {
        transaction_type: "",
        telephone_number: "",
        payer_email: "",
        payment_method: "",
        applicant_type: "",
        form_type: "",
        card_details: "",
        cvv: "",
        card_holder: "",
        card_number: "",
        expiry_month: "",
        expiry_year: "",
        dial_code: "233",
        otpCode: "",
      },

      // wizard
      currentStep: 1,
      stepTouched: false,
      formTypeOptions: [],
      formTypeLoading: false,
      showCriteriaDialog: false,
      pendingFormType: null,

      transactionId: "",
      paragraphContents: "",
      return_url: "",
      msisdn: "",
      payment_status: "Pending",
      card_otp: "",
      transaction_id: null,
      transaction_code: "",
      transaction_message: "",
      transaction_msisdn: "",
      transaction_amount: "",
      submitted: false,
      card: "CARD",
      saving: false,
      verifying: false,
      deadLine: null,
      otpSent: false,
      otpExpired: false,
      otpVerified: false,
      verifyingOtp: false,
      countDown: '00:00',
      otpCode: "",
      token: null,
    };
  },
  computed: {
    pageTitle: {
      get: function () {
        return this.$t("Purchase Application Form");
      },
    },
    steps() {
      return [
        { num: 1, label: 'Applicant Type' },
        { num: 2, label: 'Form Type' },
        { num: 3, label: 'Select Form' },
        { num: 4, label: 'Payment Method' },
        { num: 5, label: 'Confirmation' },
      ];
    },
    availablePaymentMethods() {
      if (this.formData.applicant_type === 'International') {
        return this.$menus.network_list.filter(m => m.value === 'CARD');
      }
      return this.$menus.network_list;
    },
  },
  validations() {
    let formData = {
      applicant_type: { required },
      form_type: { required },
      transaction_type: { required },
      payment_method: { required },
      dial_code: { required },
      payer_email: { required, email },
      telephone_number: { required },
      otpCode: {
        required: requiredIf(() => {
          return this.formData.payment_method && this.formData.payment_method != this.card;
        }),
      },
      cvv: {
        required: requiredIf(() => this.formData.payment_method == this.card),
      },
      card_holder: {
        required: requiredIf(() => this.formData.payment_method == this.card),
      },
      card_number: {
        required: requiredIf(() => this.formData.payment_method == this.card),
      },
      expiry_month: {
        required: requiredIf(() => this.formData.payment_method == this.card),
      },
      expiry_year: {
        required: requiredIf(() => this.formData.payment_method == this.card),
      },
    };
    return { formData };
  },
  methods: {
    goNext() {
      this.stepTouched = true;
      if (this.currentStep === 1 && !this.formData.applicant_type) {
        this.flashMsg('Please select an applicant type.', 'Step Incomplete', 'warn');
        return;
      }
      if (this.currentStep === 2 && !this.formData.form_type) {
        this.flashMsg('Please select a form type.', 'Step Incomplete', 'warn');
        return;
      }
      if (this.currentStep === 2) {
        this.loadFormTypes();
      }
      if (this.currentStep === 3 && !this.formData.transaction_type) {
        this.flashMsg('Please select an application form type.', 'Step Incomplete', 'warn');
        return;
      }
      if (this.currentStep === 4 && !this.formData.payment_method) {
        this.flashMsg('Please select a payment method.', 'Step Incomplete', 'warn');
        return;
      }
      this.stepTouched = false;
      this.currentStep++;
    },

    goBack() {
      this.stepTouched = false;
      if (this.currentStep === 3) {
        this.formData.transaction_type = '';
        this.formTypeOptions = [];
      }
      this.currentStep--;
    },


    selectPaymentMethod(value) {
      this.formData.payment_method = value;
      this.validatePaymentMethod();
    },

    loadFormTypes() {
      if (!this.formData.applicant_type || !this.formData.form_type) return;
      this.formTypeOptions = [];
      this.formData.transaction_type = '';
      this.formTypeLoading = true;
      this.$api.get(`components_data/application_fees?applicant_type=${this.formData.applicant_type}&formtype=${this.formData.form_type}`).then(
        (res) => { this.formTypeLoading = false; this.formTypeOptions = res.data; },
        () => { this.formTypeLoading = false; }
      );
    },

    selectFormType(option) {
      const matureTracks = ['Mature Exam', 'Mature Direct'];
      if (matureTracks.includes(option.entry_level)) {
        this.pendingFormType = option;
        this.showCriteriaDialog = true;
      } else {
        this.formData.transaction_type = option.value;
      }
    },

    confirmFormTypeSelection() {
      if (this.pendingFormType) {
        this.formData.transaction_type = this.pendingFormType.value;
      }
      this.showCriteriaDialog = false;
      this.pendingFormType = null;
    },

    async sendPhoneOTP() {
      this.otpExpired = false;
      this.verifying = true;
      let payload = {
        telephone_number: String(this.formData.dial_code) + String(this.formData.telephone_number).replace(/^0/, '')
      };
      this.$api.post("auth/sendPhoneOTP", payload).then(
        (response) => {
          this.verifying = false;
          let resData = response.data;
          if (resData.token) {
            this.token = resData.token;
            this.otpSent = true;
            this.otpVerified = false;
            this.formData.otpCode = '';
            this.startCountDown();
          }
        },
        (response) => {
          this.verifying = false;
          this.showPageRequestError(response);
        }
      );
    },

    async verifyOtpOnly() {
      this.verifyingOtp = true;
      try {
        const form = this.normalizedFormData();
        const res = await this.$api.post("auth/verifyphoneOTP", {
          otpCode: form.otpCode,
          msisdn: String(form.dial_code) + String(form.telephone_number).replace(/^0/, ''),
          token: this.token
        });
        if (res.data.token) {
          this.otpVerified = true;
          this.flashMsg(this.$t("OTP successfully verified."), null, "success");
        } else {
          this.formData.otpCode = '';
          this.flashMsg(this.$t("OTP validation failed. Enter OTP again to continue."), null, "error");
        }
      } catch (error) {
        this.showPageRequestError(error);
      } finally {
        this.verifyingOtp = false;
      }
    },

    startCountDown() {
      let duration = parseInt(this.$route.query.duration) || 5;
      let timer = 60 * duration;
      let minutes, seconds;
      var self = this;
      const interval = setInterval(function () {
        minutes = parseInt(timer / 60, 10);
        seconds = parseInt(timer % 60, 10);
        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;
        self.countDown = minutes + ":" + seconds;
        if (--timer < 0) {
          clearInterval(interval);
          self.otpSent = false;
          self.otpExpired = true;
        }
      }, 1000);
    },

    async verifyphoneOTP(form) {
      let payload = {
        otpCode: form.otpCode,
        msisdn: String(form.dial_code) + String(form.telephone_number).replace(/^0/, ''),
        token: this.token
      };
      this.$api.post("auth/verifyphoneOTP", payload).then(
        (response) => {
          this.saving = false;
          let resData = response.data;
          if (resData.token) {
            this.flashMsg(
              this.$t("You will receive a prompt to complete the payment process."),
              this.$t("OTP successfully verified."),
              "success"
            );
            return true;
          }
        },
        (response) => {
          this.saving = false;
          this.showPageRequestError(response);
          return false;
        }
      );
    },

    async startPayment() {
      this.submitted = true;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) {
        this.flashMsg(this.$t("please_complete_the_form"), this.$t("form_is_invalid"), "error");
        return;
      }
      this.saving = true;
      let form = this.normalizedFormData();
      let card_details = form.card_number ? form.card_number.replace(/\s/g, "") + "||" + this.$utils.upper(form.card_holder) + "||" + form.expiry_month + "||" + form.expiry_year + "||" + form.cvv : null;



      // if (form.payment_method != this.card) {
      //   let optPayload = {
      //     otpCode: form.otpCode,
      //     msisdn: String(form.dial_code) + String(form.telephone_number).replace(/^0/, ''),
      //     token: this.token
      //   }
      //   this.$api.post("auth/verifyphoneOTP", optPayload).then(
      //     (response) => {
      //       let resData = response.data;
      //       if (resData.token) {
      //         this.flashMsg(
      //           this.$t("You will receive a prompt to complete the payment process."),
      //           this.$t("OTP successfully verified."),
      //           "success"
      //         );
      //       } else {
      //         this.otpCode = null;
      //         this.flashMsg(this.$t("OTP validation failed. Enter OTP again to continue."), null, "error");
      //         return;
      //       }
      //     },
      //     (response) => {
      //       this.saving = false;
      //       this.showPageRequestError(response);
      //       return
      //     }
      //   );
      // }

      let paymentPayload = {
        transaction_type: form.transaction_type,
        telephone_number: this.token ? this.token : String(form.dial_code) + String(form.telephone_number).replace(/^0/, ''),
        payment_method: form.payment_method,
        payer_email: form.payer_email,
        card_details: card_details ? Buffer.from(card_details).toString("base64") : null,
      };

      if (paymentPayload.payment_method != this.card) {
        this.$api.post("applicants/payment", paymentPayload).then(
          (response) => {

            this.saving = false;
            let error_codes = ["1", "4", "5"];
            if (error_codes.includes(response.data.transaction_code)) {
              this.transaction_id = null;
              this.flashMsg(this.$t("payment_failed_auto_refund_initiated_please_check_funds"), null, "error");
            } else {
              this.transaction_id = response.data.transaction_id;
              this.transaction_code = response.data.transaction_code;
              this.transaction_message = response.data.transaction_message;
              this.transaction_msisdn = response.data.transaction_msisdn;
              this.transaction_amount = response.data.amount;
              this.return_url = response.data.return_url;
              if (this.transaction_code && this.transaction_code == "0" && this.transaction_message == "CARD") {
                this.payment_status = "Paid";
              }
              this.$emit("next", this.pageIndex);
            }

            this.closeDialogs();
          },
          (response) => {
            this.saving = false;
            this.showPageRequestError(response);
          }
        );
      } else {

        this.$api.post("applicants/payment", paymentPayload).then(
          (response) => {

            console.log(response.data);
            this.transactionId = response.data.TransactionID;
            this.return_url = response.data.return_url;
            let ddcForm = document.querySelector('#cardinal_collection_form');
            if (ddcForm) {
              ddcForm.action = response.data.DeviceDataCollectionUrl;
              ddcForm.elements.namedItem("JWT").value = response.data.AccessToken;
              ddcForm.submit();
              console.log("form submitted");
              console.log(response.data);
            }
          },
          (response) => {
            this.saving = false;
            this.showPageRequestError(response);
          }
        );
        window.addEventListener("message", (event) => {
          if (event.origin === "https://centinelapi.cardinalcommerce.com") {
            console.log('data', event.data);
            console.log("Starting enrollment");
            this.$api.get(`applicants/setuppaymentcard?transactionId=${this.transactionId}`)
              .then((res) => {
                console.log(res.data);
                console.log("setting up final enrollment");
                this.transaction_id = this.transactionId
                this.transaction_message == 'CARD'
                this.transaction_code = '2'
                this.saving = false
                console.log(this.transaction_id, this.transaction_code)
                // Step up process
                let stepUPIFrame = document.querySelector('#step_up_iframe');
                stepUPIFrame.style.display = "block";
                let stepUPForm = document.querySelector('#step_up_form');
                if (stepUPForm) {
                  stepUPForm.action = res.data.StepUpUrl;
                  stepUPForm.elements.namedItem("JWT").value = res.data.Jwt;
                  stepUPForm.elements.namedItem("MD").value = res.data.CustomData;
                  stepUPForm.submit();

                }
              });
          }
        }, false);

      }


    },


    async confirmPayment() {
      this.saving = true;
      let payload = { transaction_id: this.transaction_id };
      this.$api.post("payments/confirm", payload).then(
        (response) => {
          this.saving = false;
          let record = response.data;
          if (record.response_code && record.response_code == "0") {
            this.transaction_code = record.response_code;
            this.payment_status = "Paid";
            this.transaction_amount = record.amount;
            this.transaction_msisdn = record.msisdn;
          } else if (record.response_code && record.response_code == "1") {
            this.flashMsg(this.$t("Payment request unsuccessful"), null, "warn");
            this.resetForm();
          } else {
            this.flashMsg(this.$t("awaiting_payment_authorization_and_confirmation"), null, "warn");
          }
        },
        (response) => {
          this.saving = false;
          this.showPageRequestError(response);
        }
      );
    },

    validatePaymentMethod() {
      if (this.formData.payment_method != this.card) {
        this.formData.card_holder = "";
        this.formData.card_details = "";
        this.formData.card_number = "";
        this.formData.expiry_month = "";
        this.formData.expiry_year = "";
        this.formData.cvv = "";
        this.formData.telephone_number = "";
        this.formData.otpCode = "";
        this.otpSent = false;
        this.otpExpired = false;
        this.otpVerified = false;
        this.countDown = '00:00';
      }
    },

    updateFormFields: function () { },

    resetForm() {
      this.formData = {
        transaction_type: "",
        telephone_number: "",
        payer_email: "",
        payment_method: "",
        applicant_type: "",
        form_type: "",
        card_details: "",
        cvv: "",
        card_holder: "",
        card_number: "",
        expiry_month: "",
        expiry_year: "",
        dial_code: "233",
        otpCode: "",
      };
      this.currentStep = 1;
      this.stepTouched = false;
      this.formTypeOptions = [];
      this.formTypeLoading = false;
      this.transactionId = "";
      this.paragraphContents = "";
      this.return_url = "";
      this.msisdn = "";
      this.payment_status = "Pending";
      this.card_otp = "";
      this.transaction_id = null;
      this.transaction_code = "";
      this.transaction_message = "";
      this.transaction_msisdn = "";
      this.transaction_amount = "";
      this.submitted = false;
      this.saving = false;
      this.verifying = false;
      this.deadLine = null;
      this.otpSent = false;
      this.otpExpired = false;
      this.otpVerified = false;
      this.verifyingOtp = false;
      this.countDown = '00:00';
      this.otpCode = "";
      this.token = null;
    },

    getErrorClass(field) {
      if (this.v$.formData[field].$invalid && this.submitted) {
        return "p-invalid";
      }
      return "";
    },
  },
  watch: {
    'formData.applicant_type': function (value) {
      if (value === "International") {
        this.formData.payment_method = this.card;
      } else {
        this.formData.payment_method = "";
      }
      if (value) {
        this.formData.transaction_type = "";
        this.formTypeOptions = [];
      }
    },
    'formData.form_type': function (value) {
      if (value) {
        this.formData.transaction_type = '';
        this.formTypeOptions = [];
      }
    },
    'formData.otpCode': function () {
      if (this.otpVerified) {
        this.otpVerified = false;
      }
    },
  },
  async mounted() { },
  async created() { },
};
</script>

<style scoped>
li {
  margin-top: 0.5em;
  line-height: 1.4em;
}

.otp-input {
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 10px;
}

.otp-input::placeholder {
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 1px;
}

/* Step indicator */
.step-circle {
  width: 2.2rem;
  height: 2.2rem;
  font-size: 0.9rem;
  transition: background 0.2s, border-color 0.2s;
}

.step-active {
  background: var(--primary-color);
  border: 2px solid var(--primary-color);
  color: #fff;
}

.step-done {
  background: #22c55e;
  border: 2px solid #22c55e;
  color: #fff;
}

.step-pending {
  background: #fff;
  border: 2px solid #d1d5db;
  color: #9ca3af;
}

.step-label {
  max-width: 100px;
}

.step-line {
  flex: 1;
  height: 2px;
  min-width: 1.5rem;
  max-width: 3rem;
  background: #e5e7eb;
}

/* Selectable cards */
.sel-card {
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  user-select: none;
}

.sel-card--off {
  border-color: #e5e7eb;
  background: #fff;
}

.sel-card--off:hover {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.sel-card--on {
  border-color: var(--primary-color);
  background: var(--primary-50, #f0f4ff);
  box-shadow: 0 0 0 2px var(--primary-color);
}

.wizard-step-enter-active {
  animation: fadeUp 0.18s ease;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
